import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CustomerAdvanceRow } from '@tiles-erp/shared-types';
import { PageContainer } from '@tiles-erp/ui';
import { DataTable } from '../../components/DataTable';
import { useSessionBranchId } from '../../lib/session-branch';
import { useBranches } from '../products/branch-prices-api';
import { useCustomerAdvances } from './receipts-api';

const money = (value: number): string =>
  value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function CustomerAdvancesPage(): JSX.Element {
  const navigate = useNavigate();
  const branches = useBranches();
  const [branchId, setBranchId] = useSessionBranchId();
  const advances = useCustomerAdvances(branchId || undefined);
  const rows = advances.data ?? [];
  const total = rows.reduce((sum, row) => sum + row.advanceAmount, 0);
  const columns = useMemo<ColDef<CustomerAdvanceRow>[]>(() => [
    { field: 'customerName', headerName: 'Customer', minWidth: 240 },
    { field: 'phone', headerName: 'Phone', minWidth: 150 },
    {
      field: 'advanceAmount', headerName: 'Advance amount', minWidth: 170,
      cellStyle: { fontWeight: 700 }, valueFormatter: (p) => money(p.value as number),
    },
    {
      headerName: '', maxWidth: 80,
      cellRenderer: (p: ICellRendererParams<CustomerAdvanceRow>) => p.data ? (
        <Tooltip title="Open customer ledger">
          <IconButton size="small" onClick={() => navigate(`/customer-ledger?customerId=${p.data!.customerId}`)}>
            <ReceiptLongIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null,
    },
  ], [navigate]);

  return (
    <PageContainer
      title="Advance report"
      subtitle="Customers whose ledger has a credit balance from advance payments."
      actions={(
        <TextField select label="Branch" size="small" value={branchId}
          onChange={(event) => setBranchId(event.target.value)} sx={{ width: 220 }}>
          <MenuItem value="">All branches</MenuItem>
          {(branches.data ?? []).map((branch) => (
            <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
          ))}
        </TextField>
      )}
    >
      <Stack spacing={1.5}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={4}>
            <Stack><Typography variant="caption" color="text.secondary">Total advance</Typography>
              <Typography variant="h6" fontWeight={700}>₹{money(total)}</Typography></Stack>
            <Stack><Typography variant="caption" color="text.secondary">Customers</Typography>
              <Typography variant="h6" fontWeight={700}>{rows.length}</Typography></Stack>
          </Stack>
        </Paper>
        <DataTable rows={rows} columns={columns} loading={advances.isFetching} height={560} />
      </Stack>
    </PageContainer>
  );
}
